import classNames from 'classnames';
import { capitalize } from 'lodash';
import { useIntl } from 'react-intl';
import { Accordion, Checkbox, Input, Selector, SelectorOptionPropsInterface } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { ContextualDataProviderCampaign } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

import { CampaignAdditionalDetailsStyle } from './campaignAdditionalDetails.style';

import type {
  ContextualDataProviders,
  ContextualThirdPartyDataProvider
} from 'sonata-static-storage/lib/schemas/user/contextualThirdPartyDataProvider.schema';

type ContextualDataOptionsByProvider = {
  [key in ContextualThirdPartyDataProvider]?: SelectorOptionPropsInterface[];
};

export enum ProvidersEnum {
  circana = 'circana',
  mastercard = 'mastercard',
  tesela = 'tesela',
  wardem = 'wardem'
}

type Props = {
  campaignAudienceExportUserIdsEnabled: boolean;
  campaignAutoSmart: boolean;
  campaignForecastByInventoryItem: boolean;
  campaignForecastByLocation: boolean;
  campaignForecastOnly: boolean;
  campaignForecastTargetUsersValidator: boolean;
  campaignIsTemplate: boolean;
  campaignMailingType: string;
  campaignPreProdTest: boolean;
  campaignPredicioBillPacingValue: number;
  campaignSolutionsFeesEnabled: boolean;
  campaignTeselaEnabled: boolean;
  campaignTest: boolean;
  campaignTrueReachEnabled: boolean;
  contextualDataProviders: ContextualDataProviderCampaign[];
  contextualThirdPartyProviderVariables: Record<string, { group: string; id: string; name: string }[]>;
  onAudienceExportUserIdsChange: (enabled: boolean) => void;
  onCampaignAutoSmartChange: (enabled: boolean) => void;
  onCampaignTemplateCampaignChange: (enabled: boolean) => void;
  onContextualDataProviderChange: (provider: string, enabled: boolean, percentage: number, ids: string[]) => void;
  onForecastCampaignInventoryChange: (enabled: boolean) => void;
  onForecastCampaignLocationChange: (enabled: boolean) => void;
  onForecastCampaignOnlyChange: (enabled: boolean) => void;
  onForecastCampaignTargetUsersValidatorsChange: (enabled: boolean) => void;
  onPredicioBillPacingConfigChange: (price: number) => void;
  onTemplateMailingChange: (type: string) => void;
  onTestCampaignChange: (enabled: boolean) => void;
  onTestPreProdCampaignChange: (enabled: boolean) => void;
  onTrueReachConfigurationChange: (enabled: boolean) => void;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

const CONTEXTUAL_DATA_PROVIDERS_WIDTH_VARIABLES_SELECTION: ContextualThirdPartyDataProvider[] = [
  ProvidersEnum.mastercard
];

export function CampaignAdditionalDetails({
  campaignAudienceExportUserIdsEnabled,
  campaignAutoSmart,
  campaignForecastByInventoryItem,
  campaignForecastByLocation,
  campaignForecastOnly,
  campaignForecastTargetUsersValidator,
  campaignIsTemplate,
  campaignMailingType,
  campaignPreProdTest,
  campaignPredicioBillPacingValue,
  campaignSolutionsFeesEnabled,
  campaignTeselaEnabled,
  campaignTest,
  campaignTrueReachEnabled,
  contextualDataProviders = [],
  contextualThirdPartyProviderVariables,
  onAudienceExportUserIdsChange,
  onCampaignAutoSmartChange,
  onCampaignTemplateCampaignChange,
  onContextualDataProviderChange,
  onForecastCampaignInventoryChange,
  onForecastCampaignLocationChange,
  onForecastCampaignOnlyChange,
  onForecastCampaignTargetUsersValidatorsChange,
  onPredicioBillPacingConfigChange,
  onTemplateMailingChange,
  onTestCampaignChange,
  onTestPreProdCampaignChange,
  onTrueReachConfigurationChange,
  user,
  userRoles
}: Props) {
  const { formatMessage } = useIntl();
  const expanded =
    campaignTrueReachEnabled ||
    campaignTeselaEnabled ||
    campaignForecastOnly ||
    campaignForecastByLocation ||
    campaignForecastByInventoryItem ||
    campaignForecastTargetUsersValidator ||
    campaignTest ||
    campaignPreProdTest ||
    campaignIsTemplate ||
    campaignAutoSmart ||
    !!contextualDataProviders ||
    !!campaignAudienceExportUserIdsEnabled;

  const showContextualDataProviders =
    !campaignSolutionsFeesEnabled && checkUserResourcePermissions('contextualDataProviders', user, userRoles);

  const checkPermissions = (permissions: string[]) => {
    return permissions.some(permission => checkUserResourcePermissions(permission, user, userRoles));
  };

  const onChangeTemplateMailing = (selectedTemplateMailing: any) => {
    onTemplateMailingChange(selectedTemplateMailing.value);
  };

  const toggleTemplateCampaign = (enabled: boolean) => {
    if (campaignIsTemplate) {
      onTemplateMailingChange('none');
    }
    onCampaignTemplateCampaignChange(enabled);
  };

  const variablesByProvider: ContextualDataOptionsByProvider =
    Object.entries(contextualThirdPartyProviderVariables).reduce(
      (acc, [provider, variables]) => ({
        ...acc,
        ...(variables?.length > 0 && {
          [provider]: variables.map(item => ({
            group: item.group,
            id: item.id,
            label: item.name
          }))
        })
      }),
      {}
    ) || {};

  const renderContextualDataProviders = () => {
    const userContextualDataProviders = user?.contextualDataProviders?.length
      ? user.contextualDataProviders
      : ([] as ContextualDataProviders);

    const hasContextualDataProviderPermissions = checkUserResourcePermissions(
      'contextualDataProvidersPercentage',
      user,
      userRoles
    );
    const enabledContextualDataProviders = userContextualDataProviders.filter(item => {
      return (
        item.enabled &&
        (!!variablesByProvider[item.provider]?.length ||
          !CONTEXTUAL_DATA_PROVIDERS_WIDTH_VARIABLES_SELECTION.includes(item.provider))
      );
    });
    if (!hasContextualDataProviderPermissions || !enabledContextualDataProviders.length) {
      return null;
    }
    return (
      <Accordion defaultOpen={userContextualDataProviders.some(({ enabled }) => enabled)}>
        <Accordion.Header hasBorder={false}>
          <Accordion.Icon>
            {({ isOpen }: { isOpen: boolean }) => (
              <i className={classNames('fa', isOpen ? 'fa-chevron-down' : 'fa-chevron-right')} />
            )}
          </Accordion.Icon>
          {formatMessage({
            defaultMessage: 'Contextual Third Party Data Providers',
            id: 'campaignAdditionalDetails.contextualThirdPartyDataProviders'
          })}
        </Accordion.Header>
        <Accordion.Content>
          <div className="campaignAdditionalDetails__dataProvidersContainer">
            {enabledContextualDataProviders.map(providerItem => {
              const provider = contextualDataProviders?.find(i => i.provider === providerItem.provider);
              const percentage = provider ? provider.value : providerItem.value;
              const variables = variablesByProvider[providerItem.provider] || [];

              return (
                <div className="campaignAdditionalDetails__dataProvider" key={providerItem.provider}>
                  <Checkbox
                    active={!!provider}
                    classes="campaignAdditionalDetails__dataProviderCheck"
                    id={`${providerItem.provider}DataProvider`}
                    labelText={formatMessage(
                      {
                        defaultMessage: `${capitalize(providerItem.provider)} Variables`,
                        id: 'campaignAdditionalDetails.variables'
                      },
                      {
                        provider: capitalize(providerItem.provider)
                      }
                    )}
                    onClickAction={(_: string, checked: boolean) =>
                      onContextualDataProviderChange(providerItem.provider, checked, percentage, [])
                    }
                  />
                  {CONTEXTUAL_DATA_PROVIDERS_WIDTH_VARIABLES_SELECTION.includes(providerItem.provider) && (
                    <Selector
                      className="campaignAdditionalDetails__dataProviderSelector"
                      id={`${providerItem.provider}DataProviderSelector`}
                      isDisabled={!provider}
                      onChange={(val: any[]) =>
                        onContextualDataProviderChange(
                          providerItem.provider,
                          true,
                          percentage,
                          val.map(i => i.id)
                        )
                      }
                      options={variables}
                      type="multiple"
                      value={provider ? variables.filter(i => provider.segmentIds.includes(i.id as string)) : []}
                      withGrouping
                    />
                  )}
                  <Input
                    className="campaignAdditionalDetails__dataProviderInput"
                    defaultValue={percentage}
                    disabled={!provider}
                    id={`${providerItem.provider}DataProviderInput`}
                    inputAdditionalProps={{
                      max: 100,
                      min: 0
                    }}
                    name={`${providerItem.provider}DataProviderInput`}
                    numberFormat="percentage"
                    numberOfDecimals={2}
                    onFinishEdit={(val?: string) =>
                      onContextualDataProviderChange(
                        providerItem.provider,
                        true,
                        val !== undefined ? Number(val) : percentage,
                        provider ? provider.segmentIds : []
                      )
                    }
                    shape="squareUI"
                    type="text"
                  />
                </div>
              );
            })}
          </div>
        </Accordion.Content>
      </Accordion>
    );
  };

  return (
    <CampaignAdditionalDetailsStyle className="campaignAdditionalDetails">
      <Accordion defaultOpen={expanded}>
        <Accordion.Header isBold>
          {formatMessage({
            defaultMessage: 'Additional Details',
            id: 'campaignAdditionalDetails.additionalDetails'
          })}
          <Accordion.Icon />
        </Accordion.Header>
        <Accordion.Content>
          <div className="campaignAdditionalDetails__additionalDetailsContent">
            {checkUserResourcePermissions('trueReach', user, userRoles) && (
              <Accordion defaultOpen={campaignTrueReachEnabled}>
                <Accordion.Header hasBorder={false}>
                  <Accordion.Icon>
                    {({ isOpen }: { isOpen: boolean }) => (
                      <i className={classNames('fa', isOpen ? 'fa-chevron-down' : 'fa-chevron-right')} />
                    )}
                  </Accordion.Icon>
                  {formatMessage({
                    defaultMessage: 'True Reach (Extended OOH)',
                    id: 'campaignAdditionalDetails.trueReachCampaignDetails'
                  })}
                </Accordion.Header>
                <Accordion.Content>
                  <Checkbox
                    active={campaignTrueReachEnabled}
                    classes="campaignAdditionalDetails__trueReachConfiguration"
                    id="trueReachConfiguration"
                    labelText={formatMessage({
                      defaultMessage: 'True Reach (Extended OOH)',
                      id: 'campaignAdditionalDetails.trueReachCampaignDetails'
                    })}
                    onClickAction={(_: string, checked: boolean) => onTrueReachConfigurationChange(checked)}
                  />
                </Accordion.Content>
              </Accordion>
            )}
            {checkUserResourcePermissions('teselaFee', user, userRoles) && (
              <Accordion defaultOpen={campaignTeselaEnabled}>
                <Accordion.Header hasBorder={false}>
                  <Accordion.Icon>
                    {({ isOpen }: { isOpen: boolean }) => (
                      <i className={classNames('fa', isOpen ? 'fa-chevron-down' : 'fa-chevron-right')} />
                    )}
                  </Accordion.Icon>
                  {formatMessage({
                    defaultMessage: 'Location Planning',
                    id: 'campaignAdditionalDetails.locationPlanningCampaignDetails'
                  })}
                </Accordion.Header>
                <Accordion.Content>
                  <Checkbox
                    active={campaignTeselaEnabled}
                    classes="campaignAdditionalDetails__locationPlanningConfiguration"
                    id="teselaConfiguration"
                    labelText={formatMessage({
                      defaultMessage: 'Audience Finder (Tesela)',
                      id: 'campaignAdditionalDetails.locationPlanningConfiguration'
                    })}
                    onClickAction={(_: string, checked: boolean) =>
                      onContextualDataProviderChange(ProvidersEnum.tesela, checked, 100, [])
                    }
                  />
                </Accordion.Content>
              </Accordion>
            )}
            {checkPermissions([
              'campaignForecastOnly',
              'campaignForecastByLocation',
              'campaignForecastByInventoryItem',
              'campaignForecastTargetUserValidator'
            ]) && (
              <Accordion
                defaultOpen={
                  campaignForecastOnly ||
                  campaignForecastByLocation ||
                  campaignForecastByInventoryItem ||
                  campaignForecastTargetUsersValidator
                }
              >
                <Accordion.Header hasBorder={false}>
                  <Accordion.Icon>
                    {({ isOpen }: { isOpen: boolean }) => (
                      <i className={classNames('fa', isOpen ? 'fa-chevron-down' : 'fa-chevron-right')} />
                    )}
                  </Accordion.Icon>
                  {formatMessage({
                    defaultMessage: 'Additional Details',
                    id: 'campaignAdditionalDetails.forecastCampaignDetails'
                  })}
                </Accordion.Header>
                <Accordion.Content>
                  <div className="campaignAdditionalDetails__forecastConfigContainer">
                    {checkUserResourcePermissions('campaignForecastOnly', user, userRoles) && (
                      <Checkbox
                        active={campaignForecastOnly}
                        classes="campaignAdditionalDetails__forecastCampaignOnly"
                        id="forecastCampaignOnly"
                        labelText={formatMessage({
                          defaultMessage: 'Forecast Campaign',
                          id: 'campaignAdditionalDetails.forecastCampaignOnly'
                        })}
                        onClickAction={(_: string, checked: boolean) => onForecastCampaignOnlyChange(checked)}
                      />
                    )}
                    {checkUserResourcePermissions('campaignForecastByLocation', user, userRoles) && (
                      <Checkbox
                        active={campaignForecastByLocation}
                        classes="campaignAdditionalDetails__forecastCampaignLocation"
                        id="forecastCampaignLocation"
                        labelText={formatMessage({
                          defaultMessage: 'Forecast By Campaign Location',
                          id: 'campaignAdditionalDetails.forecastCampaignLocation'
                        })}
                        onClickAction={(_: string, checked: boolean) => onForecastCampaignLocationChange(checked)}
                      />
                    )}
                    {checkUserResourcePermissions('campaignForecastByInventoryItem', user, userRoles) && (
                      <Checkbox
                        active={campaignForecastByInventoryItem}
                        classes="campaignAdditionalDetails__forecastCampaignInventory"
                        id="forecastCampaignInventory"
                        labelText={formatMessage({
                          defaultMessage: 'Forecast By Campaign Inventory Item',
                          id: 'campaignAdditionalDetails.forecastCampaignInventory'
                        })}
                        onClickAction={(_: string, checked: boolean) => onForecastCampaignInventoryChange(checked)}
                      />
                    )}
                    {checkUserResourcePermissions('campaignForecastTargetUserValidator', user, userRoles) && (
                      <Checkbox
                        active={campaignForecastTargetUsersValidator}
                        classes="campaignAdditionalDetails__forecastCampaignTargetUsersValidator"
                        id="forecastCampaignTargetUsersValidator"
                        labelText={formatMessage({
                          defaultMessage: 'Target Users Validator',
                          id: 'campaignAdditionalDetails.forecastCampaignTargetUsersValidator'
                        })}
                        onClickAction={(_: string, checked: boolean) =>
                          onForecastCampaignTargetUsersValidatorsChange(checked)
                        }
                      />
                    )}
                  </div>
                </Accordion.Content>
              </Accordion>
            )}
            {checkPermissions(['campaignTesting', 'campaignPreProdTesting']) && (
              <Accordion defaultOpen={campaignTest || campaignPreProdTest}>
                <Accordion.Header hasBorder={false}>
                  <Accordion.Icon>
                    {({ isOpen }: { isOpen: boolean }) => (
                      <i className={classNames('fa', isOpen ? 'fa-chevron-down' : 'fa-chevron-right')} />
                    )}
                  </Accordion.Icon>
                  {formatMessage({
                    defaultMessage: 'Test Configuration',
                    id: 'campaignAdditionalDetails.testCampaignDetails'
                  })}
                </Accordion.Header>
                <Accordion.Content>
                  <div className="campaignAdditionalDetails__testConfigContainer">
                    {checkUserResourcePermissions('campaignTesting', user, userRoles) && (
                      <Checkbox
                        active={campaignTest}
                        classes="campaignAdditionalDetails__testCampaign"
                        id="testCampaign"
                        labelText={formatMessage({
                          defaultMessage: 'Test Campaign',
                          id: 'campaignAdditionalDetails.testCampaign'
                        })}
                        onClickAction={(_: string, checked: boolean) => onTestCampaignChange(checked)}
                      />
                    )}
                    {checkUserResourcePermissions('campaignPreProdTesting', user, userRoles) && (
                      <Checkbox
                        active={campaignPreProdTest}
                        classes="campaignAdditionalDetails__testPreProdCampaign"
                        id="testPreProdCampaign"
                        labelText={formatMessage({
                          defaultMessage: 'PreProd Test Campaign',
                          id: 'campaignAdditionalDetails.testPreProdCampaign'
                        })}
                        onClickAction={(_: string, checked: boolean) => onTestPreProdCampaignChange(checked)}
                      />
                    )}
                  </div>
                </Accordion.Content>
              </Accordion>
            )}
            {checkUserResourcePermissions('campaignTemplate', user, userRoles) && (
              <Accordion defaultOpen={campaignIsTemplate}>
                <Accordion.Header hasBorder={false}>
                  <Accordion.Icon>
                    {({ isOpen }: { isOpen: boolean }) => (
                      <i className={classNames('fa', isOpen ? 'fa-chevron-down' : 'fa-chevron-right')} />
                    )}
                  </Accordion.Icon>
                  {formatMessage({
                    defaultMessage: 'Template Configuration',
                    id: 'campaignAdditionalDetails.templateCampaignDetails'
                  })}
                </Accordion.Header>
                <Accordion.Content>
                  <div className="campaignAdditionalDetails__templateCampaignDetailsContainer">
                    <Checkbox
                      active={campaignIsTemplate}
                      classes="campaignAdditionalDetails__templateCampaign"
                      id={'templateCampaign'}
                      labelText={formatMessage({
                        defaultMessage: 'Template Campaign',
                        id: 'campaignAdditionalDetails.templateCampaign'
                      })}
                      onClickAction={(_: string, checked: boolean) => toggleTemplateCampaign(checked)}
                    />
                    <Selector
                      className="campaignAdditionalDetails__templateMailingSelector"
                      isDisabled={!campaignIsTemplate}
                      label={formatMessage({
                        defaultMessage: 'Template Mailing:',
                        id: 'campaignAdditionalDetails.templateMailing'
                      })}
                      onChange={onChangeTemplateMailing}
                      options={[
                        {
                          id: 'none',
                          label: formatMessage({
                            defaultMessage: 'NONE',
                            id: 'campaignAdditionalDetails.none'
                          })
                        },
                        {
                          id: 'creation',
                          label: formatMessage({
                            defaultMessage: 'CREATION',
                            id: 'campaignAdditionalDetails.creation'
                          })
                        },
                        {
                          id: 'update',
                          label: formatMessage({
                            defaultMessage: 'UPDATE',
                            id: 'campaignAdditionalDetails.update'
                          })
                        },
                        {
                          id: 'both',
                          label: formatMessage({
                            defaultMessage: 'BOTH',
                            id: 'campaignAdditionalDetails.both'
                          })
                        }
                      ]}
                      type="simple"
                      value={[
                        {
                          id: 'Mailing type',
                          label: formatMessage({
                            defaultMessage: campaignMailingType,
                            id: `campaignAdditionalDetails.${campaignMailingType}`
                          })
                        }
                      ]}
                    />
                  </div>
                </Accordion.Content>
              </Accordion>
            )}
            {checkUserResourcePermissions('autoSmart', user, userRoles) && (
              <Accordion defaultOpen={campaignAutoSmart}>
                <Accordion.Header hasBorder={false}>
                  <Accordion.Icon>
                    {({ isOpen }: { isOpen: boolean }) => (
                      <i className={classNames('fa', isOpen ? 'fa-chevron-down' : 'fa-chevron-right')} />
                    )}
                  </Accordion.Icon>
                  {formatMessage({
                    defaultMessage: 'AutoSmart Configuration',
                    id: 'campaignAdditionalDetails.autoSmartCampaignDetails'
                  })}
                </Accordion.Header>
                <Accordion.Content>
                  <Checkbox
                    classes="campaignAdditionalDetails__autoSmart"
                    defaultActive={campaignAutoSmart}
                    id="autoSmartCampaignDetails"
                    labelText={formatMessage({
                      defaultMessage: 'AutoSmart',
                      id: 'campaignAdditionalDetails.autoSmart'
                    })}
                    onClickAction={(_: string, checked: boolean) => onCampaignAutoSmartChange(checked)}
                  />
                </Accordion.Content>
              </Accordion>
            )}
            {checkUserResourcePermissions('audienceDataBillPacing', user, userRoles) && (
              <Accordion>
                <Accordion.Header hasBorder={false}>
                  <Accordion.Icon>
                    {({ isOpen }: { isOpen: boolean }) => (
                      <i className={classNames('fa', isOpen ? 'fa-chevron-down' : 'fa-chevron-right')} />
                    )}
                  </Accordion.Icon>
                  {formatMessage({
                    defaultMessage: 'Audience Data Provider Pixel Configuration',
                    id: 'campaignAdditionalDetails.audienceBillPacingCampaignDetails'
                  })}
                </Accordion.Header>
                <Accordion.Content>
                  <div className="campaignAdditionalDetails__predicioPixelUsage">
                    <Input
                      defaultValue={campaignPredicioBillPacingValue}
                      id={'predicioPixelUsage'}
                      inputAdditionalProps={{
                        max: 100,
                        min: 0
                      }}
                      name="predicioPixelUsage"
                      numberFormat="percentage"
                      onFinishEdit={(val?: string) => {
                        if (val !== undefined) {
                          onPredicioBillPacingConfigChange(Number(val));
                        }
                      }}
                      shape="squareUI"
                      type="number"
                    />
                  </div>
                </Accordion.Content>
              </Accordion>
            )}
            {checkUserResourcePermissions('automatedUserIdReports', user, userRoles) && (
              <Accordion defaultOpen={!!campaignAudienceExportUserIdsEnabled}>
                <Accordion.Header hasBorder={false}>
                  <Accordion.Icon>
                    {({ isOpen }: { isOpen: boolean }) => (
                      <i className={classNames('fa', isOpen ? 'fa-chevron-down' : 'fa-chevron-right')} />
                    )}
                  </Accordion.Icon>
                  {formatMessage({
                    defaultMessage: 'Automated User Id Report',
                    id: 'campaignAdditionalDetails.audienceExportUserIds'
                  })}
                </Accordion.Header>
                <Accordion.Content>
                  <Checkbox
                    active={!!campaignAudienceExportUserIdsEnabled}
                    classes="campaignAdditionalDetails__audienceExportUserIdsSelector"
                    id="audienceExportUserIds"
                    labelText={formatMessage({
                      defaultMessage: 'Enabled',
                      id: 'campaignAdditionalDetails.audienceExportUserIdsCheckbox'
                    })}
                    onClickAction={(_: string, checked: boolean) => onAudienceExportUserIdsChange(checked)}
                  />
                </Accordion.Content>
              </Accordion>
            )}
            {showContextualDataProviders && renderContextualDataProviders()}
          </div>
        </Accordion.Content>
      </Accordion>
    </CampaignAdditionalDetailsStyle>
  );
}
